import React from 'react';
import { Tabs, Tab } from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useTranslation } from 'react-i18next';
import { McpSetupClient } from '../lib/clients';
import styles from './mcp-clients.module.scss';

function ClientIcon({ client, className = '' }: { client: McpSetupClient; className?: string }) {
  if (client === 'claudeDesktop') {
    return <img src="/images/integrations/claude-white.svg" alt="" width="20" height="20" className={`${styles.brandIcon} ${styles.claudeIcon} ${className}`} aria-hidden="true" />;
  }
  if (client === 'chatgpt' || client === 'cursor') {
    const light = client === 'chatgpt' ? 'openai-blossom-black' : 'cursor-light';
    const dark = client === 'chatgpt' ? 'openai-blossom-white' : 'cursor-dark';
    return (
      <span className={`${styles.brandIcon} ${client === 'chatgpt' ? styles.openAiIcon : ''} ${className}`} aria-hidden="true">
        {/* Official SVGs stay unmodified; the adjoining name labels the icon. */}
        <img src={`/images/integrations/${light}.svg`} alt="" width="20" height="20" className={styles.lightIcon} />
        <img src={`/images/integrations/${dark}.svg`} alt="" width="20" height="20" className={styles.darkIcon} />
      </span>
    );
  }
  // Claude Code and Codex are terminal clients: a generic icon, no brand mark.
  return <TerminalIcon className={`${styles.brandIcon} ${className}`} aria-hidden="true" />;
}

interface ClientTabsProps<T extends McpSetupClient> {
  id: string;
  label: string;
  /** The hero preview and the setup section list different clients. */
  clients: readonly T[];
  value: T;
  onChange: (client: T) => void;
}

export default function ClientTabs<T extends McpSetupClient>({ id, label, clients, value, onChange }: ClientTabsProps<T>) {
  const { t } = useTranslation('start');
  return (
    <Tabs
      className={styles.tabs}
      value={value}
      onChange={(_, client: T) => onChange(client)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      selectionFollowsFocus
      aria-label={label}
    >
      {clients.map((client) => (
        <Tab
          key={client}
          value={client}
          id={`${id}-tab-${client}`}
          aria-controls={`${id}-panel-${client}`}
          icon={<ClientIcon client={client} />}
          iconPosition="start"
          label={t(`mcp.setup.tabs.${client}.label`)}
        />
      ))}
    </Tabs>
  );
}
